import { useMemo, useState } from 'react';
import { createSafeContext } from '@mews/framework';
import { getCurrentEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector } from '@mews-commander/utils';
import { PaymentRequestType, SimpleAmount } from '@mews-commander/data-access';
import { useGetAccountingConfigurationOptionsSuspense } from '../../../queries';
import { DEFAULT_PAYMENT_REQUEST_TYPE_OPTIONS } from './const';

interface DefaultFormValues {
    customerId: string;
    billId?: string;
    serviceOrderId?: string | null;
    currencyCode: string;
    amount?: number;
    invoiceIds?: string[];
    paymentPlanId?: string | null;
    paymentMethodId?: string | null;
}

export interface ReadOnlyFormFields {
    isPaymentPlanIdReadOnly?: boolean;
    isServiceOrderIdReadOnly?: boolean;
    isPaymentMethodIdReadOnly?: boolean;
}

interface DefaultFormContextProps {
    defaultFormValues: DefaultFormValues;
    readOnlyFormFields: ReadOnlyFormFields;
    billGrossAmount?: DefaultFormValues['amount'];
    setSelectedCurrencyCode: React.Dispatch<React.SetStateAction<DefaultFormValues['currencyCode']>>;
    setSelectedAmount: React.Dispatch<React.SetStateAction<DefaultFormValues['amount']>>;
    setServiceOrderId: React.Dispatch<React.SetStateAction<DefaultFormValues['serviceOrderId']>>;
    setSelectedInvoiceIds: (invoiceIds: DefaultFormValues['invoiceIds']) => void;
    setPaymentPlanId: React.Dispatch<React.SetStateAction<DefaultFormValues['paymentPlanId']>>;
    canLinkInvoicesConfig: boolean;
    isLinkingInvoiceDropdownVisible?: boolean;
    accountId: string;
    billId?: string;
    ownerIds?: string[];
    allowedPaymentRequestTypeOptions: PaymentRequestType[];
}

const { DefaultFormContextProvider, useDefaultFormContext } = createSafeContext<
    'DefaultFormContext',
    DefaultFormContextProps
>('DefaultFormContext');

interface DefaultFormProviderProps {
    accountId: string;
    ownerIds?: string[];
    billId?: string;
    canLinkInvoices?: boolean;
    isLinkingInvoiceDropdownVisible?: boolean;
    defaultSelectedFormValues?: DefaultSelectedFormValues;
    readOnlyFormFields?: ReadOnlyFormFields;
    allowedPaymentRequestTypeOptions?: PaymentRequestType[];
    billAmount?: SimpleAmount;
}

export interface DefaultSelectedFormValues {
    invoiceIds?: string[];
    paymentPlanId?: string;
    serviceOrderId?: string;
    paymentMethodId?: string;
}

export const DefaultFormProvider: React.FC<React.PropsWithChildren<DefaultFormProviderProps>> = ({
    children,
    accountId,
    ownerIds,
    billId,
    canLinkInvoices,
    isLinkingInvoiceDropdownVisible = true,
    defaultSelectedFormValues,
    readOnlyFormFields,
    allowedPaymentRequestTypeOptions,
    billAmount,
}) => {
    const defaultCurrency = useCommanderSelector<string>(getCurrentEnterpriseDefaultCurrencyCode);
    const { data: accountingConfigurationOptions } = useGetAccountingConfigurationOptionsSuspense();

    // @ts-expect-error The selector props type is incorrect
    const isVirtualBill = useCommanderSelector((state) => isBillVirtual(state, { id: billId }));

    const canLinkInvoicesConfig = Boolean(accountingConfigurationOptions?.receivableTrackingEnabled && canLinkInvoices);
    const billGrossAmount = !canLinkInvoicesConfig && billId ? billAmount?.value : undefined;

    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(defaultSelectedFormValues?.invoiceIds);
    const [selectedCurrencyCode, setSelectedCurrencyCode] =
        useState<DefaultFormValues['currencyCode']>(defaultCurrency);
    const [selectedAmount, setSelectedAmount] = useState<DefaultFormValues['amount']>(billGrossAmount);
    const [serviceOrderId, setServiceOrderId] = useState<DefaultFormValues['serviceOrderId']>(
        defaultSelectedFormValues?.serviceOrderId
    );
    const [paymentPlanId, setPaymentPlanId] = useState<DefaultFormValues['paymentPlanId']>(
        defaultSelectedFormValues?.paymentPlanId
    );

    const values: DefaultFormContextProps = useMemo(
        () => ({
            defaultFormValues: {
                serviceOrderId,
                customerId: accountId,
                ownerIds,
                billId: !billId || isVirtualBill ? undefined : billId,
                currencyCode: selectedCurrencyCode,
                amount: selectedAmount,
                invoiceIds: selectedInvoiceIds,
                paymentPlanId,
                paymentMethodId: defaultSelectedFormValues?.paymentMethodId,
                isLinkingInvoiceDropdownVisible,
            },
            readOnlyFormFields: {
                isPaymentPlanIdReadOnly: readOnlyFormFields?.isPaymentPlanIdReadOnly,
                isServiceOrderIdReadOnly:
                    readOnlyFormFields?.isPaymentPlanIdReadOnly || readOnlyFormFields?.isServiceOrderIdReadOnly,
                isPaymentMethodIdReadOnly: readOnlyFormFields?.isPaymentMethodIdReadOnly,
            },
            billGrossAmount,
            setServiceOrderId,
            setSelectedAmount,
            setSelectedCurrencyCode,
            canLinkInvoicesConfig,
            isLinkingInvoiceDropdownVisible,
            accountId,
            ownerIds,
            billId,
            setSelectedInvoiceIds,
            setPaymentPlanId,
            allowedPaymentRequestTypeOptions: allowedPaymentRequestTypeOptions ?? DEFAULT_PAYMENT_REQUEST_TYPE_OPTIONS,
        }),
        [
            accountId,
            ownerIds,
            billGrossAmount,
            billId,
            isVirtualBill,
            selectedAmount,
            selectedCurrencyCode,
            serviceOrderId,
            canLinkInvoicesConfig,
            selectedInvoiceIds,
            paymentPlanId,
            defaultSelectedFormValues,
            readOnlyFormFields,
            isLinkingInvoiceDropdownVisible,
            allowedPaymentRequestTypeOptions,
        ]
    );

    return <DefaultFormContextProvider value={values}>{children}</DefaultFormContextProvider>;
};

export { useDefaultFormContext };
